import React, { useRef, useState } from "react";
import Slider from "react-slick";
import {
  Card,
  CardMedia,
  Typography,
  Button,
  Box,
  Grid,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";


export default function ResidentialGridView({
  id,
  name,
  address,
  price,
  content,
  thumbnail,
  images = [],
  nearby = [],
  facilities = [],
  amenities = [],
  perSqft,
  area,
  categoryname,
  verify,
  country,
  soldStatus,
  sold_status,
}) {
  const isSold = soldStatus === 1 || sold_status === 1;
  const previewSliderRef = useRef(null);
  const navi = useNavigate();
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const visibleAmenities = amenities.slice(0, 3);
  const visibleNearby = nearby.slice(0, 3);

  // Build full images list
  const allImages =
    Array.isArray(images) && images.length > 0
      ? images
      : thumbnail
        ? [thumbnail]
        : ["/assets/images/placeholder_img.png"];

  const showNavArrows = allImages.length > 1;

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredVideo, setHoveredVideo] = useState(null);

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = document.querySelector(`video[src="${src}"]`);
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src) {
          const otherVideo = document.querySelector(`video[src="${playingVideo}"]`);
          if (otherVideo) otherVideo.pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", bgcolor: "rgba(0,0,0,0.85)" },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{ position: "absolute", top: { xs: 10, sm: 16 }, right: { xs: 10, sm: 20 }, zIndex: 10, color: "#fff", bgcolor: "rgba(0,0,0,0.4)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: "relative", height: { xs: 300, sm: 480, md: 600 }, overflow: "hidden" }}>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}
            <Slider
              ref={previewSliderRef}
              initialSlide={activeIndex}
              beforeChange={(oldIndex, newIndex) => setActiveIndex(newIndex)}
              dots
              infinite
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
            >
              {allImages.map((img, i) => (
                <Box key={i}>
                  <Box sx={{ height: { xs: 300, sm: 480, md: 600 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.7)", p: 1 }}>
                    {isVideo(img) ? (
                      <video
                        src={img}
                        controls
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload nopictureinpicture"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <img src={img} alt={`${name || ""} ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
            {/* slide counter badge */}
            {showNavArrows && (
              <Box sx={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", bgcolor: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "12px", px: 1.5, py: 0.3, fontSize: 12, pointerEvents: "none", zIndex: 3 }}>
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Card
        onClick={() => navi(`/flats-villas/details/${id}`)}
        sx={{
          borderRadius: 4,
          p: 2,
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          height: "100%",
          backgroundColor: "#FBFBF8",
          boxShadow: "0 4px 12px 6px rgba(0,0,0,0.08)",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box display="flex" flexDirection="column" flexGrow={1} gap={1}>
          {allImages[0] && (
            <Box sx={{ position: "relative" }}>
              {isSold && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    bgcolor: "#d32f2f",
                    color: "#fff",
                    px: 2,
                    py: 0.6,
                    borderRadius: 1.5,
                    fontSize: 13,
                    fontWeight: 800,
                    zIndex: 3,
                    textTransform: "uppercase",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                    letterSpacing: "0.5px",
                  }}
                >
                  Sold Out
                </Box>
              )}
              <Box
                sx={{
                  px: 0.5,
                  opacity: isSold ? 0.6 : 1,
                  filter: isSold ? "grayscale(20%)" : "none",
                }}
              >
                {isVideo(allImages[0]) ? (
                  <Box
                    onMouseEnter={() => setHoveredVideo(allImages[0])}
                    onMouseLeave={() => setHoveredVideo(null)}
                    sx={{ position: "relative", width: "100%", height: 170 }}
                  >
                    <CardMedia
                      component="video"
                      src={allImages[0]}
                      playsInline
                      disablePictureInPicture
                      disableRemotePlayback
                      controlsList="nodownload nopictureinpicture"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePlay(e, allImages[0]);
                      }}
                      sx={{
                        width: "100%",
                        height: 170,
                        borderRadius: 3,
                        objectFit: "cover",
                        cursor: "pointer",
                        display: "block",
                      }}
                    />
                    {(playingVideo !== allImages[0] || hoveredVideo === allImages[0]) && (
                      <IconButton
                        onClick={(e) => togglePlay(e, allImages[0])}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "white",
                          borderRadius: "4px",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        {playingVideo === allImages[0] ? <PauseIcon /> : <PlayArrowIcon />}
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    image={allImages[0]}
                    onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                    sx={{
                      width: "100%",
                      height: 170,
                      borderRadius: 3,
                      objectFit: "cover",
                      cursor: "zoom-in",
                      transition: "transform 0.3s ease",
                      "&:hover": { transform: "scale(1.04)" },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}


          <Box sx={{ opacity: isSold ? 0.65 : 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {/* TITLE + CATEGORY BUTTON */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box sx={{ flex: 1, pr: 1 }}>
                <Typography fontWeight={600}>{name || "Property Name"}</Typography>
                <Typography
                  fontSize={13}
                  color="#707070"
                  sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.5 }}
                >
                  {address || "No Location available"} {Number(country) === 3 ? ", India" : Number(country) === 2 ? ", Dubai" : ""}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={(e) => e.stopPropagation()}
                sx={{ bgcolor: "#95B160", borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}
              >
                <ApartmentIcon fontSize="small" /> {categoryname || "Villa"}
              </Button>
              {country && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    color: "#95B160",
                    borderColor: "#95B160",
                    borderRadius: 2,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    "&:hover": { borderColor: "#95B160", bgcolor: "rgba(149, 177, 96, 0.04)" }
                  }}
                >
                  <LocationOnIcon fontSize="small" /> {Number(country) === 3 ? "India" : Number(country) === 2 ? "Dubai" : "Other"}
                </Button>
              )}
            </Box>

            <Box>
              {verify === 1 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <img src="/assets/images/verified.png" alt="verified" width={20} height={20} />
                  <Typography fontSize={{ xs: 12, sm: 13 }} fontWeight={600}>Verified Plot</Typography>
                </Box>
              )}
            </Box>

            <Divider flexItem sx={{ borderStyle: "3px solid", borderColor: "#95B160" }} />

            {/* PRICE ROW */}
            <Grid container sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
              <PriceItem label="Total Price" value={`${price || "-"}`} />
              <VDivider />
              <PriceItem label="Per Sq.ft" value={perSqft || "-"} />
              <VDivider />
              <PriceItem label="Total Sq.ft" value={area || "-"} icon={<OpenWithIcon sx={{ fontSize: 14 }} />} />
            </Grid>

            <Divider flexItem sx={{ borderStyle: "3px solid", borderColor: "#95B160" }} />

            {/* AMENITIES */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: 16, lineHeight: "22px", whiteSpace: "nowrap" }}>Amenities:</Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, wordBreak: "break-word" }}>
                {visibleAmenities.length > 0
                  ? visibleAmenities.map((item, index) => (
                    <span key={item.id || index}>{item.title}{index !== visibleAmenities.length - 1 && " • "}</span>
                  ))
                  : "No nearby amenities"}
              </Typography>
            </Box>

            {/* NEARBY */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: 16, lineHeight: "22px", whiteSpace: "nowrap" }}>Nearby:</Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, wordBreak: "break-word" }}>
                {visibleNearby.length > 0
                  ? visibleNearby.map((item, index) => (
                    <span key={item.id || index}>{item.title}{index !== visibleNearby.length - 1 && " • "}</span>
                  ))
                  : "No nearby locations"}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" justifyContent="center" mt="auto" pt={2}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#7EAE23", color: "#fff", borderRadius: 16, px: { xs: 6, sm: 10, md: 10 }, py: 1, textTransform: "capitalize", fontSize: { xs: 13, sm: 14 }, opacity: isSold ? 0.65 : 1 }}
              onClick={(e) => { e.stopPropagation(); navi(`/flats-villas/details/${id}`); }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
}

const PriceItem = ({ label, value, icon }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.5} color="#95B160">
      {icon || <CurrencyRupeeIcon sx={{ fontSize: 14 }} />}
      <Typography fontSize={14} fontWeight={600}>{value}</Typography>
    </Box>
    <Typography fontSize={14}>{label}</Typography>
  </Box>
);

const VDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ borderStyle: "dashed", borderColor: "#95B160", mx: 0.5 }} />
);
